import React, { useState } from "react";
import Modal from "../../shared/Modal";
import { useTheme } from '../../../contexts/ThemeContext';
import logger from '../../../utils/logger';

const EmailComposerModal = ({ isOpen, onClose, client, onSend }) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const { isDarkMode } = useTheme();

  if (!isOpen || !client) return null;

  // Check in multiple possible locations for email
  const emailAddress =
    client?.contact?.email ||
    client?.email ||
    client?.ContactInformation?.email;

  // For debugging
  logger.info("Email composer opened for client:", client);
  logger.info("Client contact info:", client.contact);
  logger.info("Using email address:", emailAddress);

  const handleSend = () => {
    onSend({
      to: emailAddress,
      subject,
      message,
    });
    setSubject("");
    setMessage("");
  };

  const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim();

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      maxWidth="2xl"
      className="bg-white dark:bg-gray-800 transform transition-all duration-200 shadow-xl"
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white transition-colors duration-200">Compose Email</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="space-y-4">
          <div className="flex flex-col">
            <label className="text-sm text-gray-600 dark:text-gray-400 mb-1 transition-colors duration-200">To:</label>
            <input
              type="text"
              value={
                emailAddress
                  ? `${fullName} <${emailAddress}>`
                  : `${fullName} (No email available)`
              }
              readOnly
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm text-gray-600 dark:text-gray-400 mb-1 transition-colors duration-200">Subject:</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500 transition-all duration-200"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm text-gray-600 dark:text-gray-400 mb-1 transition-colors duration-200">Message:</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={6}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500 transition-all duration-200"
            />
          </div>
          <div className="flex justify-end space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={!emailAddress}
              className={`px-4 py-2 text-white rounded-lg transition-all duration-200 transform hover:scale-[1.02] ${
                emailAddress 
                ? "bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600" 
                : "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
              }`}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EmailComposerModal;
